import { Button } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import {
	addChangesHistory,
	createTicket,
	deleteTicket,
	getTicketsByUserId,
} from 'store/ticket-service/asyncActions';
import {
	getActiveTicket,
	getIsTicketUpdate,
	getTicketUpdating,
	getTickets,
} from 'store/ticket-service/selector';
import {
	addLocalTicket,
	removeLocalTicket,
	setActiveTicket,
	setIsTicketUpdate,
} from 'store/ticket-service/ticketSlice';
import { TicketType } from 'store/ticket-service/types';
import { triggerTicketSave } from 'store/user-service/userSlice';

import { useEventBus } from 'utils/EventBus';

import { HistoryActionTypes, NamespacesList, TicketStages } from 'core/enums/common';

import { InfoModal } from 'components/shared/Modal/Info';

import { ReactComponent as Error } from 'assets/icons/error.svg';

type CreateTicketModalProps = {
	handleCloseModal: () => void;
	ticket: TicketType;
	userId: string;
	isCreating?: boolean;
};

export const CreateTicketModal: FC<CreateTicketModalProps> = ({
	handleCloseModal,
	ticket,
	userId,
	isCreating,
}) => {
	const { t: modalTranslate } = useTranslation(NamespacesList['modals']);

	const [open, setOpen] = useState<boolean>(true);
	const [isTicketLoading, setIsTicketLoading] = useState<boolean>(false);
	const dispatch = useAppDispatch();
	const { addEventListener, removeEventListener } = useEventBus();

	const updating = useSelector(getTicketUpdating);
	const tickets = useSelector(getTickets);
	const activeTicket = useSelector(getActiveTicket);
	const isTicketUpdate = useSelector(getIsTicketUpdate);

	useEffect(() => {
		addEventListener('successfulSave', handleClose);

		return () => {
			removeEventListener('successfulSave', handleClose);
		};
	}, [activeTicket?.id, isTicketUpdate, activeTicket]);

	const handleClose = () => {
		if (!isTicketLoading) {
			handleCloseModal();
			setOpen(false);
		}
	};

	const handleSave = () => {
		if (activeTicket) {
			if (!activeTicket.id) {
				setIsTicketLoading(true);

				dispatch(createTicket({ userId, ticket }))
					.then(() => dispatch(getTicketsByUserId({ userId })))
					.then((res: any) => {
						dispatch(setActiveTicket(res?.payload?.data[0]));

						dispatch(
							addChangesHistory({
								userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
								ticketId: res.payload.data[0].id,
								ticketStep: TicketStages.general,
								actionType: HistoryActionTypes.ticketCreate,
							})
						);

						isCreating && createNewLocalTicket();
						setIsTicketLoading(false);
						handleClose();
					});
			}

			if (isTicketUpdate) dispatch(triggerTicketSave());
			else handleClose();
		}
	};

	const createNewLocalTicket = () => {
		const defaultTicket = {
			title: 'New request',
			description: '',
			createdDate: new Date().toISOString(),
			comment: null,
			messages: [],
			status: 0,
			currentStep: TicketStages.general,
			ticketSummaries: [],
			userStoryTests: [],
			useCases: [],
		};
		dispatch(addLocalTicket(defaultTicket));
		dispatch(setActiveTicket(defaultTicket));
	};

	const handleDelete = async () => {
		if (activeTicket) {
			if (!activeTicket.id) {
				dispatch(removeLocalTicket());
				handleClose();
			} else {
				setIsTicketLoading(true);

				// @ts-ignore
				await dispatch(deleteTicket(activeTicket.id));

				const recordsPerPage = tickets.length;

				dispatch(getTicketsByUserId({ userId, recordsPerPage })).then(() => {
					setIsTicketLoading(false);
					handleClose();
					dispatch(setIsTicketUpdate(false));
				});
			}
		}
	};

	return (
		<InfoModal
			isOpen={open}
			onClose={handleClose}
			image={<Error />}
			title={modalTranslate('createTicketModal.title')}
			description={modalTranslate('createTicketModal.description')}
			buttons={
				<>
					<Button
						sx={{
							height: '44px',
							width: { xs: '45%', sm: '215px' },
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: '700',
							marginTop: '24px',
						}}
						variant="outlined"
						color="error"
						disabled={isTicketLoading || updating}
						onClick={handleSave}
					>
						{updating || isTicketLoading ? (
							<Oval
								height={24}
								width={24}
								color="#fff"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="#6e6e6e"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						) : (
							modalTranslate('createTicketModal.confirm')
						)}
					</Button>

					<Button
						sx={{
							height: '44px',
							width: { xs: '45%', sm: '215px' },
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: '700',
							marginTop: '24px',
						}}
						variant="contained"
						color="error"
						disabled={isTicketLoading || updating}
						onClick={handleDelete}
					>
						{updating || isTicketLoading ? (
							<Oval
								height={24}
								width={24}
								color="#fff"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="#6e6e6e"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						) : (
							modalTranslate('createTicketModal.cancel')
						)}
					</Button>
				</>
			}
		></InfoModal>
	);
};
